import { Component, OnInit, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { LanguageService } from '@shared/services/language.service';
import { ModalService } from '@shared/services/modal.service';
import { catchError, forkJoin, map, of } from 'rxjs';
import { ModalPersonalDataInfoComponent } from 'src/app/register/modal-personal-data-info/modal-personal-data-info.component';

/** What {@link ModalPersonalDataInfoComponent} falls back to when a slot has no PDF yet. */
const STATIC_FALLBACK: Record<string, Record<string, string>> = {
  TERMS: {
    it: '/assets/terms/ICAM_CondizioniGeneraliUtilizzo_MyICAM.pdf',
    en: '/assets/terms/ICAM_CondizioniGeneraliUtilizzo_MyICAM_EN.pdf'
  },
  PRIVACY: {
    it: '/assets/terms/ICAM_PrivacyPolicy_MyICAM.pdf',
    en: '/assets/terms/ICAM_PrivacyPolicy_MyICAM_EN.pdf'
  },
  COOKIE: {
    it: '/assets/terms/ICAM_CookiePolicy_MyICAM.pdf',
    en: '/assets/terms/ICAM_CookiePolicy_MyICAM_EN.pdf'
  }
};

export interface LegalDocumentPreviewRow {
  type: string;
  lang: string;
  /** False when the public endpoint has no PDF for the slot: the user gets the bundled asset. */
  fromRegistry: boolean;
  htmlLength: number;
  fileName?: string;
  contentType?: string;
  modifiedDate?: string;
  /** The URL the user's browser actually opens for this document. */
  fileUrl: string;
  unreachable: boolean;
}

interface PublicLegalDocumentMeta {
  type: string;
  lang: string;
  htmlContent?: string;
  hasFile: boolean;
  fileName?: string;
  contentType?: string;
  modifiedDate?: string;
}

/**
 * Admin-only preview of the legal documents exactly as end users receive them.
 * <p>
 * It reads the same public endpoint the registration modal reads (no admin token), so what it
 * shows is what an anonymous visitor gets, and it opens the very same modal component in each
 * of the three modes the application uses it in.
 */
@Component({
  selector: 'app-legal-documents-preview',
  templateUrl: './legal-documents-preview.component.html',
  styleUrl: './legal-documents-preview.component.scss'
})
export class LegalDocumentsPreviewComponent implements OnInit {
  private _http = inject(HttpClient);
  private _modalService = inject(ModalService);
  private _language = inject(LanguageService);

  readonly types = ['TERMS', 'PRIVACY', 'COOKIE'];
  readonly languages = ['it', 'en'];

  rows: LegalDocumentPreviewRow[] = [];
  isLoading = true;

  get currentLang(): string {
    return this._language.getCurrent();
  }

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.isLoading = true;

    const calls = this.types.flatMap((type) => this.languages.map((lang) => this.fetchRow(type, lang)));

    forkJoin(calls).subscribe((rows) => {
      this.rows = rows;
      this.isLoading = false;
    });
  }

  private fetchRow(type: string, lang: string) {
    return this._http
      .get<PublicLegalDocumentMeta>(`${environment.apiBaseUrl}/public/legal-documents`, {
        params: { type, lang }
      })
      .pipe(
        map((meta) => this.toRow(type, lang, meta, false)),
        catchError(() => of(this.toRow(type, lang, undefined, true)))
      );
  }

  private toRow(
    type: string,
    lang: string,
    meta: PublicLegalDocumentMeta | undefined,
    unreachable: boolean
  ): LegalDocumentPreviewRow {
    const fromRegistry = !!meta?.hasFile;

    return {
      type,
      lang,
      fromRegistry,
      htmlLength: meta?.htmlContent?.length || 0,
      fileName: meta?.fileName,
      contentType: meta?.contentType,
      modifiedDate: meta?.modifiedDate,
      fileUrl: fromRegistry
        ? `${environment.apiBaseUrl}/public/legal-documents/file?type=${type}&lang=${lang}`
        : STATIC_FALLBACK[type][lang],
      unreachable
    };
  }

  switchLanguage(lang: string): void {
    this._language.setCurrent(lang);
    this.load();
  }

  /** The modal as an anonymous visitor sees it on the registration page. */
  openRegistrationModal(): void {
    this._modalService.open(ModalPersonalDataInfoComponent, { previewMode: true }, { size: 'lg' });
  }

  /** The blocking modal a user must go through on first login, with the three checkboxes. */
  openAcceptanceModal(): void {
    this._modalService.open(
      ModalPersonalDataInfoComponent,
      { showBtns: true, showClose: true, previewMode: true },
      { size: 'lg' }
    );
  }

  /** The modal reached from the account page, which also lists the three PDF links. */
  openAccountModal(): void {
    this._modalService.open(
      ModalPersonalDataInfoComponent,
      { showPdfLinks: true, previewMode: true },
      { size: 'lg' }
    );
  }

  get allFromRegistry(): boolean {
    return this.rows.length > 0 && this.rows.every((row) => row.fromRegistry);
  }

  get anyUnreachable(): boolean {
    return this.rows.some((row) => row.unreachable);
  }
}
